import type { DocumentNode } from 'graphql';
import gql from 'graphql-tag';

import { executeGqlQuery, type GqlCallVariables } from 'src/graphql/client';
import type { ID } from 'src/lib/collection';
import { getGqlFragmentName, getSingleProp } from 'src/lib/graphql';

interface Variables extends GqlCallVariables {
  id: ID;
}

interface Options {
  list?: boolean;
}

/**
 * Queries the result object of a job using the given GraphQL fragment.
 */
export const queryJobResult = async <T = unknown>(
  variables: Variables,
  gqlFragment: DocumentNode,
  options?: Options
): Promise<T | T[] | undefined> => {
  const fragmentName = getGqlFragmentName(gqlFragment);
  if (!fragmentName) {
    throw new Error('GQL fragment must have a name when used in "queryJobResult"');
  }

  const isListResult = options?.list ?? false;
  const query = gql`
    query JobEntityResult($id: ID!) {
      jobStatus(id: $id) {
        id
        ${isListResult ? 'resultList' : 'resultObject'} {
          ...${fragmentName}
        }
      }
    }
    ${gqlFragment}
  `;

  const response = await executeGqlQuery({
    query,
    variables,
  });

  const result = getSingleProp<{
    resultList?: T[];
    resultObject?: T;
  }>(response.data);
  return isListResult ? result?.resultList : result?.resultObject;
};
